// src/components/Products/Cardapio/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  listCardapios,
  listCategorias,
  listItens,
  saveCardapio,
  saveCategoria,
  saveItem,
  getRestaurante,
  saveRestaurante,
  listPedidos,
  addItemPedidoDashboard,
  updateItemPedidoDashboard,
  removeItemPedidoDashboard,
  listComplementos,
  saveComplemento,
  deleteComplemento,
  getItem,
  getIfoodImportStatus,
} from '../../../../services/api';
import { Add, Palette, Tune, SwapVert, Storefront } from '@mui/icons-material';
import { House } from 'lucide-react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { toast } from 'react-toastify';

import DashboardHeader from '../components/DashboardHeader';
import IfoodCatalogImportDialog from '../components/IfoodCatalogImportDialog';
import ComplementosPanel from '../components/ComplementosPanel';
import DesignPanel from '../components/DesignPanel';
import DashboardModals from '../components/DashboardModals';
import MenuTreePanel from '../components/MenuTreePanel';
import {
  reorder,
  idKey,
  includes,
  getNome,
  getDescricao,
  toNum,
  formatBRLDisplay,
  calcTotalPedido,
  menuPalette,
  CARDAPIO_VIRTUAL_STORAGE_KEY,
  CHANNEL_IN_PERSON,
  normalizeMenuChannel,
  getChannelMeta,
  normalizeCanais,
  buildChannelsWithActive,
  applyCardapioChannelView,
  applyItemChannelView,
} from '../utils/dashboardHelpers';
import { buildDeleteDialogMessage } from '../components/DeleteDialogMessage';
import {
  buildAvailabilityForm,
  DEFAULT_AVAILABILITY_RULE,
  validateAvailabilityForm,
} from '../utils/cardapioAvailability';

/* -------------------- Componente Principal -------------------- */
const Dashboard = () => {
  const [cardapios, setCardapios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [restaurante, setRestaurante] = useState(null);
  const [restauranteHeader, setRestauranteHeader] = useState({ nome: '', cardapio_bg_color: '', cardapio_bg_image_url: '', cardapio_logo_url: '', cardapio_layout: 'modern' });
  const [savingRestaurante, setSavingRestaurante] = useState(false);
  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [availabilityModal, setAvailabilityModal] = useState({
    open: false,
    cardapio: null,
    form: {
      enabled: false,
      regras: [{ ...DEFAULT_AVAILABILITY_RULE, dias: [...DEFAULT_AVAILABILITY_RULE.dias] }],
      mostrarForaDoHorario: true,
    },
  });
  const [formState, setFormState] = useState({ open: false, type: null, data: {}, parent: {} });
  const navigate = useNavigate();
  
  const [complementos, setComplementos] = useState([]);
  const [compForm, setCompForm] = useState({ id: null, nome: '', preco: 0, precoInput: '', ativo: true });
  const [showComplementos, setShowComplementos] = useState(false);
  const [usarCardapioVirtual, setUsarCardapioVirtual] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(CHANNEL_IN_PERSON);
  const [ifoodConnected, setIfoodConnected] = useState(false);
  const [ifoodMerchantId, setIfoodMerchantId] = useState('');
  const [ifoodImportOpen, setIfoodImportOpen] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [expandedCardapioId, setExpandedCardapioId] = useState(undefined);
  const [pedidoAddItem, setPedidoAddItem] = useState({ item: null, comps: [] });
const [pedidoModal, setPedidoModal] = useState({ open: false, pedido: null, compSelecao: {}, itemSelecao: null });
const pedidoModalData = useMemo(() => {
  if (!pedidoModal.open) return null;
  const found = (pedidos || []).find((p) => p.id === pedidoModal.pedido?.id);
  return found || pedidoModal.pedido;
}, [pedidoModal, pedidos]);

  const confirmActionRef = React.useRef(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    confirmColor: 'error',
  });
  const openConfirmDialog = useCallback(({ title, message, confirmText, confirmColor, onConfirm }) => {
    confirmActionRef.current = onConfirm || null;
    setConfirmDialog({
      open: true,
      title: title || 'Confirmar ação',
      message: message || '',
      confirmText: confirmText || 'Confirmar',
      confirmColor: confirmColor || 'error',
    });
  }, []);
  const closeConfirmDialog = useCallback(() => {
    confirmActionRef.current = null;
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);
  const handleConfirmDialogConfirm = useCallback(async () => {
    const fn = confirmActionRef.current;
    closeConfirmDialog();
    if (typeof fn === 'function') {
      await fn();
    }
  }, [closeConfirmDialog]);

  const allItems = useMemo(
    () =>
      (cardapios || []).flatMap((c) =>
        (c.categorias || []).flatMap((cat) =>
          (cat.itens || []).map((it) => ({
            ...it,
            nome: getNome(it),
            cardapioNome: getNome(c),
            categoriaNome: getNome(cat),
          }))
        )
      ),
    [cardapios]
  );
  const selectedChannelMeta = useMemo(() => getChannelMeta(selectedChannel), [selectedChannel]);
  const cardapiosForSelectedChannel = useMemo(
    () =>
      (cardapios || []).map((cardapio) =>
        applyCardapioChannelView(
          {
            ...cardapio,
            categorias: (cardapio.categorias || []).map((categoria) => ({
              ...categoria,
              itens: (categoria.itens || []).map((item) => applyItemChannelView(item, selectedChannel)),
            })),
          },
          selectedChannel
        )
      ),
    [cardapios, selectedChannel]
  );


  const openAvailability = (cardapio) => {
    setAvailabilityModal({
      open: true,
      cardapio,
      form: buildAvailabilityForm(cardapio?.disponibilidade),
    });
  };

  const closeAvailability = () =>
    setAvailabilityModal({
      open: false,
      cardapio: null,
      form: buildAvailabilityForm(null),
    });

  const handleSaveAvailability = async () => {
    const { cardapio, form } = availabilityModal;
    if (!cardapio?.id) return;
    const validationError = validateAvailabilityForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      await saveCardapio({ id: cardapio.id, disponibilidade: form });
      setCardapios((prev) =>
        prev.map((c) => (c.id === cardapio.id ? { ...c, disponibilidade: form } : c))
      );
      closeAvailability();
      toast.success('Horários do cardápio atualizados.');
    } catch (e) {
      console.error('Erro ao salvar disponibilidade', e);
      toast.error(e?.response?.data?.message || 'Não foi possível salvar os horários do cardápio.');
    }
  };

  const loadData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      if (!silent) setError(null);

      const cardapiosBase = await listCardapios();

      const data = await Promise.all(
        cardapiosBase.map(async (cardapio) => {
          const categoriasBase = await listCategorias(cardapio.id);
          const cardNome = getNome(cardapio);

          const categoriasCompletas = await Promise.all(
            categoriasBase.map(async (categoria) => {
              const itensBase = await listItens(categoria.id);
              const catNome = getNome(categoria);

              const itensCompletos = await Promise.all(
                itensBase.map(async (item) => ({
                  ...item,
                  nome: getNome(item),
                  descricao: getDescricao(item),
                  traducoes: [{ nome: getNome(item), descricao: getDescricao(item) }],
                  categoria_ids: item.categoria_ids || [categoria.id],
                }))
              );

              return {
                ...categoria,
                nome: catNome,
                cardapio_nome: cardNome,
                traducoes: [{ nome: catNome }],
                itens: itensCompletos,
              };
            })
          );

          return {
            ...cardapio,
            nome: cardNome,
            traducoes: [{ nome: cardNome }],
            categorias: categoriasCompletas,
          };
        })
      );

      // preenche ordem padrão
      data.forEach((c, i) => {
        if (c.ordem == null) c.ordem = i;
        c.categorias?.forEach((cat, j) => {
          if (cat.ordem == null) cat.ordem = j;
          cat.itens?.forEach((it, k) => { if (it.ordem == null) it.ordem = k; });
        });
      });

      setCardapios(data.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)));
    } catch (e) {
      console.error('Erro ao carregar dados', e);
      if (silent) {
        toast.error('Nao foi possivel atualizar a lista do One Menu.');
        return;
      }
      setError('Não foi possível carregar os dados. Verifique a API e tente novamente.');
    } finally {
      if (!silent) setLoading(false);
    }
  };


  useEffect(() => {
    loadData();
    const fetchRestaurante = async () => {
      try {
        const r = await getRestaurante();
        const resto = r?.data || r;
        setRestaurante(resto);
        setRestauranteHeader({
          nome: resto?.nome || '',
          cardapio_bg_color: resto?.cardapio_bg_color || '',
          cardapio_bg_image_url: resto?.cardapio_bg_image_url || '',
          cardapio_logo_url: resto?.cardapio_logo_url || '',
          cardapio_layout: resto?.cardapio_layout || 'modern',
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchRestaurante();
    const fetchComplementos = async () => {
      try {
        const data = await listComplementos();
        setComplementos(data || []);
      } catch (e) {
        console.error('Erro ao listar complementos', e);
      }
    };
    fetchComplementos();

    const fetchIfoodStatus = async () => {
      try {
        const status = await getIfoodImportStatus();
        setIfoodConnected(Boolean(status?.connected && status?.merchant?.id));
        setIfoodMerchantId(status?.merchant?.id || '');
      } catch (_error) {
        setIfoodConnected(false);
        setIfoodMerchantId('');
      }
    };
    fetchIfoodStatus();
  }, []);


  useEffect(() => {
    if (!restaurante?.id || typeof window === 'undefined') return;
    try {
      const saved = window.localStorage.getItem(`${CARDAPIO_VIRTUAL_STORAGE_KEY}:${restaurante.id}`);
      if (saved == null) {
        setUsarCardapioVirtual(true);
        return;
      }
      setUsarCardapioVirtual(saved === '1');
    } catch (e) {
      console.error('Erro ao recuperar preferencia de cardapio virtual', e);
    }
  }, [restaurante?.id]);

  const toggleUsarCardapioVirtual = useCallback(() => {
    setUsarCardapioVirtual((prev) => {
      const next = !prev;
      if (!next) {
        setShowHeaderConfig(false);
        setReorderMode(false);
      }
      if (restaurante?.id && typeof window !== 'undefined') {
        window.localStorage.setItem(`${CARDAPIO_VIRTUAL_STORAGE_KEY}:${restaurante.id}`, next ? '1' : '0');
      }
      return next;
    });
  }, [restaurante?.id]);

  const handleSaveRestauranteHeader = async () => {
    if (!restaurante?.id) return;
    const nome = restauranteHeader.nome.trim();
    if (!nome) {
      toast.warning('Informe o nome do restaurante.');
      return;
    }
    setSavingRestaurante(true);
    try {
      await saveRestaurante({
        id: restaurante.id,
        nome,
        cardapio_bg_color: restauranteHeader.cardapio_bg_color || '',
        cardapio_bg_image_url: restauranteHeader.cardapio_bg_image_url || '',
        cardapio_logo_url: restauranteHeader.cardapio_logo_url || '',
        cardapio_layout: restauranteHeader.cardapio_layout || 'modern',
      });
      const updated = await getRestaurante();
      const resto = updated?.data || updated;
      setRestaurante(resto);
      setRestauranteHeader({
        nome: resto?.nome || '',
        cardapio_bg_color: resto?.cardapio_bg_color || '',
        cardapio_bg_image_url: resto?.cardapio_bg_image_url || '',
        cardapio_logo_url: resto?.cardapio_logo_url || '',
        cardapio_layout: resto?.cardapio_layout || 'modern',
      });
      toast.success('Dados do cardápio público salvos.');
    } catch (e) {
      console.error('Falha ao salvar cabeçalho do cardápio', e);
      toast.error('Não foi possível salvar os dados do cardápio público.');
    } finally {
      setSavingRestaurante(false);
    }
  };

  const handleSaveComplemento = async () => {
    try {
      const isEdit = !!compForm.id;
      const payload = {
        id: compForm.id,
        nome: compForm.nome || 'Complemento',
        preco: Number(compForm.preco) || 0,
        ativo: !!compForm.ativo,
      };
      const saved = await saveComplemento(payload);
      setComplementos((list) => {
        const exists = list.find((c) => c.id === saved.id);
        if (exists) return list.map((c) => (c.id === saved.id ? saved : c));
        return [...list, saved];
      });
      setCompForm({ id: null, nome: '', preco: 0, precoInput: '', ativo: true });
      toast.success(isEdit ? 'Complemento atualizado.' : 'Complemento criado.');
    } catch (e) {
      console.error('Erro ao salvar complemento', e);
      toast.error('N\u00e3o foi poss\u00edvel salvar o complemento.');
    }
  };

  const handleDeleteComplemento = async (id) => {
    openConfirmDialog({
      title: 'Excluir complemento',
      message: buildDeleteDialogMessage('Ao excluir este complemento, ele será removido permanentemente.'),
      confirmText: 'Excluir complemento',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteComplemento(id);
          setComplementos((list) => list.filter((c) => c.id !== id));
          toast.success('Complemento exclu\u00eddo.');
        } catch (e) {
          console.error('Erro ao excluir complemento', e);
          toast.error('N\u00e3o foi poss\u00edvel excluir o complemento.');
        }
      },
    });
  };

  const handleDelete = async (delFn, id, dialogConfig) => {
    const config = typeof dialogConfig === 'string'
      ? {
          title: 'Confirmar exclusão',
          message: buildDeleteDialogMessage(dialogConfig),
          confirmText: 'Excluir',
          confirmColor: 'error',
        }
      : {
          title: dialogConfig?.title || 'Confirmar exclusão',
          message: dialogConfig?.message || buildDeleteDialogMessage(dialogConfig?.description || 'Esta ação removerá o item selecionado permanentemente.'),
          confirmText: dialogConfig?.confirmText || 'Excluir',
          confirmColor: dialogConfig?.confirmColor || 'error',
        };

    openConfirmDialog({
      ...config,
      onConfirm: async () => {
        try {
          await delFn(id);
          loadData();
        } catch (err) {
          console.error('Falha ao excluir', err);
          alert('Não foi possível realizar a exclusão.');
        }
      },
    });
  };

  const stats = useMemo(() => {
    let categoriasCount = 0;
    let itensCount = 0;
    cardapios.forEach(c => {
      categoriasCount += c.categorias?.length || 0;
      c.categorias?.forEach(cat => {
        itensCount += cat.itens?.length || 0;
      });
    });
    return { cardapios: cardapios.length, categorias: categoriasCount, itens: itensCount };
  }, [cardapios]);

  /* -------------------- filtro (memo) -------------------- */
  const filteredCardapios = useMemo(() => {
    const q = filterText.trim();
    if (!q) return cardapiosForSelectedChannel;

    return cardapiosForSelectedChannel
      .map((card) => {
        const cardNome = getNome(card);
        const cardMatch = includes(cardNome, q);

        const categorias = (card.categorias || []).map((cat) => {
          const catNome = getNome(cat);
          const catMatch = includes(catNome, q);

          let itens = cat.itens || [];
          if (!catMatch && !cardMatch) {
            itens = itens.filter((it) => {
              const nome = getNome(it);
              const desc = getDescricao(it);
              return includes(nome, q) || includes(desc, q);
            });
          }
          return { ...cat, itens };
        }).filter((c) => c.itens?.length || includes(getNome(c), q));

        return { ...card, categorias };
      })
      .filter((c) => c.categorias?.length || includes(getNome(c), q));
  }, [cardapiosForSelectedChannel, filterText]);

  const allCategorias = useMemo(() => cardapios.flatMap(c => c.categorias || []), [cardapios]);
  const allComplementos = useMemo(() => complementos || [], [complementos]);


  const normalizeIds = (arr) => (Array.isArray(arr) ? arr.map((id) => Number(id)) : []);
  const uniq = (arr) => Array.from(new Set(arr.filter((v) => v != null)));
  

  const refreshPedidos = useCallback(async () => {
    try {
      const data = await listPedidos();
      const hydrated = (data || []).map((p) => ({
        ...p,
        total: toNum(p.total) || calcTotalPedido(p),
        itens: p.itens || [],
      }));
      setPedidos(hydrated);
      setPedidoModal((pm) => {
        if (pm.open && pm.pedido) {
          const found = hydrated.find((p) => p.id === pm.pedido.id);
          return { ...pm, pedido: found || pm.pedido };
        }
        return pm;
      });
    } catch (e) {
      console.error('Erro ao carregar pedidos', e);
    }
  }, []);

  const handleAddItemPedido = async (pedidoId, item, complementosIds = []) => {
    if (!item) return;
    try {
      await addItemPedidoDashboard(pedidoId, {
        item_id: item.id,
        quantidade: 1,
        preco_unit: Number(item.preco) || 0,
        complementos: complementosIds.map((id) => ({ id, preco: (allComplementos.find((c) => c.id === id)?.preco) || 0 })),
      });
      await refreshPedidos();
    } catch (e) {
      console.error('Erro ao adicionar item no pedido', e);
      alert('Não foi possível adicionar o item.');
    }
  };

  const handleUpdateItemQtd = async (pedidoId, pedidoItemId, nextQtd) => {
    if (!pedidoItemId || nextQtd < 1) return;
    try {
      await updateItemPedidoDashboard(pedidoId, pedidoItemId, { quantidade: nextQtd });
      await refreshPedidos();
    } catch (e) {
      console.error('Erro ao atualizar item do pedido', e);
      alert('Não foi possível atualizar a quantidade.');
    }
  };

  const handleRemoveItemPedido = async (pedidoId, pedidoItemId) => {
    if (!pedidoItemId) return;
    try {
      await removeItemPedidoDashboard(pedidoId, pedidoItemId);
      await refreshPedidos();
    } catch (e) {
      console.error('Erro ao remover item do pedido', e);
      alert('Não foi possível remover o item.');
    }
  };
  const handleUpdateItemComplementos = async (pedidoId, pedidoItemId, nextCompIds = []) => {
    try {
      await updateItemPedidoDashboard(pedidoId, pedidoItemId, {
        complementos: nextCompIds.map((id) => ({
          id,
          preco: (allComplementos.find((c) => c.id === id)?.preco) || 0,
        })),
      });
      await refreshPedidos();
    } catch (e) {
      console.error('Erro ao atualizar complementos', e);
      toast.error('Não foi possível atualizar os complementos.');
    }
  };

  useEffect(() => {
    if (!cardapios.length) {
      setExpandedCardapioId(undefined);
      return;
    }
    if (expandedCardapioId === undefined) {
      setExpandedCardapioId(cardapios[0].id);
      return;
    }
    if (
      expandedCardapioId !== null &&
      !cardapios.some((c) => c.id === expandedCardapioId)
    ) {
      setExpandedCardapioId(cardapios[0].id);
    }
  }, [cardapios, expandedCardapioId]);

  const openForm = async (type, data = {}, parent = {}) => {
    const baseData = {
      ativo: true,
      categoria_ids: [],
      complemento_ids: [],
      complementos: [],
      tipo: 'DEFAULT',
      grupos_opcoes: [],
      ...data,
      preco: toNum(data.preco),
      precoInput: data.preco != null ? formatBRLDisplay(data.preco) : '',
    };
    if (type === 'cardapio' || type === 'item') {
      baseData.canais = normalizeCanais(baseData.canais, baseData.ativo !== false);
    }
    if (type === 'item' && data?.id) {
      try {
        const full = await getItem(data.id);
        const catIds = uniq(normalizeIds([...(full.categoria_ids || []), parent.categoria_id]));
        setFormState({
          open: true,
          type,
          data: {
            ...baseData,
            ...full,
            canais: normalizeCanais(full.canais, full.ativo !== false),
            categoria_ids: catIds.length ? catIds : baseData.categoria_ids,
            categoria_id: full.categoria_id || catIds[0] || baseData.categoria_id,
            complemento_ids: normalizeIds(full.complemento_ids) || [],
            complementos: full.complementos || [],
            precoInput: full.preco != null ? formatBRLDisplay(full.preco) : baseData.precoInput,
          },
          parent,
        });
        return;
      } catch (e) {
        console.error('Erro ao buscar item', e);
      }
    }
    setFormState({
      open: true,
      type,
      data: baseData,
      parent,
    });
  };

  const closeForm = () => setFormState({ open: false, type: null, data: {}, parent: {} });
  const closePedidoModal = () => setPedidoModal({ open: false, pedido: null, compSelecao: {}, itemSelecao: null });

  const handleSaveForm = async () => {
    try {
      const { type, data, parent } = formState;
      if (type === 'cardapio') {
        await saveCardapio({
          id: data.id,
          nome: data.nome || '',
          ativo: data.ativo ?? true,
          restaurante_id: data.restaurante_id || restaurante?.id,
          aceita_pedido: data.aceita_pedido ?? false,
          canais: normalizeCanais(data.canais, data.ativo ?? true),
        });
      }
      if (type === 'categoria') {
        await saveCategoria({
          id: data.id,
          nome: data.nome || '',
          ativo: data.ativo ?? true,
          cardapio_id: data.cardapio_id || parent.cardapio_id,
        });
      }
      if (type === 'item') {
        await saveItem({
          id: data.id,
          nome: data.nome || '',
          descricao: data.descricao || '',
          preco: Number(data.preco) || 0,
          ativo: data.ativo ?? true,
          categoria_ids: data.categoria_ids?.length ? data.categoria_ids : (data.categoria_id ? [data.categoria_id] : parent.categoria_id ? [parent.categoria_id] : []),
          categoria_id: data.categoria_id || data.categoria_ids?.[0] || parent.categoria_id,
          codigo: data.codigo || '',
          foto_url: data.foto_url || '',
          tipo: data.tipo || 'DEFAULT',
          regra_preco_pizza: data.regra_preco_pizza || 'MAIOR_SABOR',
          grupos_opcoes: data.grupos_opcoes || [],
          complemento_ids: data.complemento_ids || [],
          complementos: (data.complemento_ids || []).map((cid) => {
            const cfg = (data.complementos || []).find((c) => c.id === cid) || {};
            return {
              id: cid,
              obrigatorio: !!cfg.obrigatorio,
              min_qtd: cfg.min_qtd != null ? Number(cfg.min_qtd) : (cfg.obrigatorio ? 1 : 0),
              max_qtd: cfg.max_qtd != null ? Number(cfg.max_qtd) : null,
            };
          }),
          canais: normalizeCanais(data.canais, data.ativo ?? true),
        });
      }
      closeForm();
      await loadData();
    } catch (e) {
      console.error('Falha ao salvar', e);
      const message = e?.response?.data?.message
        || e?.response?.data?.error
        || 'Não foi possível salvar. Revise os dados e tente novamente.';
      toast.error(message);
    }
  };

  /* -------------------- Alternar "ativo" do item (otimista + rollback) -------------------- */
  const toggleItemActive = async (categoriaId, item, nextActive) => {
    const itemId = item?.id;
    if (!itemId) return;
    const channel = normalizeMenuChannel(selectedChannel);
    const prev = cardapios;

    // otimista
    setCardapios((curr) =>
      curr.map((card) => ({
        ...card,
        categorias: (card.categorias || []).map((cat) =>
          String(cat.id) === String(categoriaId)
            ? {
                ...cat,
                itens: (cat.itens || []).map((it) =>
                  String(it.id) === String(itemId)
                    ? {
                        ...it,
                        ativo: nextActive ? true : it.ativo,
                        canais: buildChannelsWithActive(it, channel, nextActive),
                      }
                    : it
                ),
              }
            : cat
        ),
      }))
    );

    try {
      const payload = {
        id: itemId,
        canais: buildChannelsWithActive(item, channel, nextActive),
      };
      if (nextActive && item.ativo_base === false) {
        payload.ativo = true;
      }
      await saveItem(payload);
    } catch (e) {
      console.error('Falha ao atualizar ativo do item', e);
      alert('Não foi possível salvar o status do item.');
      setCardapios(prev); // rollback
    }
  };

  /* -------------------- DnD: drag end -------------------- */
  const onDragEnd = (result) => {
    if (filterText) return;
    const { destination, source, type } = result;
    if (!destination) return;
    if (destination.droppableId !== source.droppableId || destination.index === source.index) return;

    if (type === 'CARDAPIO') {
      const newList = reorder(cardapios, source.index, destination.index).map((c, idx) => ({ ...c, ordem: idx }));
      setCardapios(newList);
      return;
    }

    if (type === 'CATEGORIA') {
      const [, cIdStr] = source.droppableId.split('-');
      const cardIndex = cardapios.findIndex(c => idKey(c) === cIdStr);
      if (cardIndex < 0) return;
      const card = cardapios[cardIndex];
      const newCats = reorder(card.categorias || [], source.index, destination.index).map((cat, idx) => ({ ...cat, ordem: idx }));
      const newCardapios = [...cardapios];
      newCardapios[cardIndex] = { ...card, categorias: newCats };
      setCardapios(newCardapios);
      return;
    }

    if (type === 'ITEM') {
      const [, catIdStr] = source.droppableId.split('-');
      const cardIndex = cardapios.findIndex(c => (c.categorias || []).some(cat => idKey(cat) === catIdStr));
      if (cardIndex < 0) return;
      const card = cardapios[cardIndex];
      const catIndex = (card.categorias || []).findIndex(cat => idKey(cat) === catIdStr);
      if (catIndex < 0) return;
      const categoria = card.categorias[catIndex];
      const newItens = reorder(categoria.itens || [], source.index, destination.index).map((it, idx) => ({ ...it, ordem: idx }));
      const newCats = [...card.categorias];
      newCats[catIndex] = { ...categoria, itens: newItens };
      const newCardapios = [...cardapios];
      newCardapios[cardIndex] = { ...card, categorias: newCats };
      setCardapios(newCardapios);
    }
  };

  /* -------------------- persistência da ordem (payload mínimo) -------------------- */
  const persistOrder = async () => {
    if (savingOrder) return;
    setSavingOrder(true);
    try {
      for (const [idx, c] of cardapios.entries()) {
        await saveCardapio({ id: c.id, ordem: idx });
      }
      for (const c of cardapios) {
        const categorias = c.categorias || [];
        for (const [j, cat] of categorias.entries()) {
          await saveCategoria({ id: cat.id, ordem: j });
          const itens = cat.itens || [];
          for (const [k, it] of itens.entries()) {
            await saveItem({ id: it.id, ordem: k });
          }
        }
      }
      setReorderMode(false);
      await loadData();
      toast.success('Nova ordem salva com sucesso.');
    } catch (e) {
      console.error('Falha ao salvar nova ordem', e);
      toast.error('Não foi possível salvar a nova ordem.');
    } finally {
      setSavingOrder(false);
    }
  };

  const cancelOrder = async () => {
    setReorderMode(false);
    await loadData();
  };

  /* -------------------- render -------------------- */
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress /> <Typography ml={2}>Carregando menu...</Typography>
      </Box>
    );
  }
  if (error) return (
    <Box p={3}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>Ops, algo deu errado</Typography>
        <Typography color="text.secondary">{error}</Typography>
      </Paper>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          px: { xs: 1.5, sm: 2.5, md: 4 },
          py: { xs: 2.5, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 1120, mx: 'auto', position: 'relative', pb: 4 }}>
          <Tooltip title="Voltar para produtos">
            <IconButton
              onClick={() => navigate('/products')}
              aria-label="Voltar para produtos"
              sx={{
                mb: 1,
                width: 44,
                height: 44,
                color: menuPalette.blue,
                border: `1px solid ${menuPalette.border}`,
                backgroundColor: 'rgba(255,255,255,0.9)',
                transition: 'all .2s ease',
                '&:hover': {
                  backgroundColor: menuPalette.blue,
                  color: '#fff',
                  borderColor: menuPalette.blue,
                },
              }}
            >
              <House size={22} strokeWidth={2.2} />
            </IconButton>
          </Tooltip>
          <DashboardHeader
            stats={stats}
            reorderMode={reorderMode}
            setReorderMode={setReorderMode}
            onSaveOrder={persistOrder}
            onCancelOrder={cancelOrder}
            savingOrder={savingOrder}
            filterText={filterText}
            setFilterText={setFilterText}
            restaurante={restaurante}
            usarCardapioVirtual={usarCardapioVirtual}
            selectedChannel={selectedChannel}
            onSelectedChannelChange={setSelectedChannel}
            ifoodConnected={ifoodConnected}
            onOpenIfoodImport={() => setIfoodImportOpen(true)}
          />

          <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={1.25}
              mb={2.5}
            >
              {/* Grupo 1 — Status do cardápio digital */}
              <Tooltip title={usarCardapioVirtual ? 'O cardápio digital está ativo e visível para seus clientes. Clique para desativar.' : 'O cardápio digital está desativado. Clique para ativá-lo.'}>
                <Button
                  size="small"
                  variant={usarCardapioVirtual ? 'contained' : 'outlined'}
                  color={usarCardapioVirtual ? 'success' : 'primary'}
                  onClick={toggleUsarCardapioVirtual}
                  startIcon={<Storefront />}
                  sx={{ fontWeight: 700 }}
                >
                  {usarCardapioVirtual ? 'Cardápio virtual ligado' : 'Utilizar página de cardápio'}
                </Button>
              </Tooltip>

              {/* Grupo 2 — Ferramentas de configuração (abre/fecha painel) */}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5, borderColor: menuPalette.border, display: { xs: 'none', md: 'block' } }} />
              {usarCardapioVirtual && (
                <Tooltip title={showHeaderConfig ? 'Ocultar painel de design' : 'Personalizar cor, logo e layout do cardápio'}>
                  <Button
                    size="small"
                    variant={showHeaderConfig ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setShowHeaderConfig((v) => !v)}
                    startIcon={<Palette />}
                    sx={{ fontWeight: 700 }}
                  >
                    Design
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={showComplementos ? 'Ocultar painel de complementos' : 'Gerenciar adicionais que podem ser vinculados aos itens'}>
                <Button
                  size="small"
                  variant={showComplementos ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setShowComplementos((v) => !v)}
                  startIcon={<Tune />}
                  sx={{ fontWeight: 700 }}
                >
                  Complementos
                </Button>
              </Tooltip>

              {/* Grupo 3 — Ações (alinhadas à direita) */}
              <Box display="flex" gap={1} alignItems="center" sx={{ ml: { md: 'auto' }, flexWrap: 'wrap' }}>
                {usarCardapioVirtual && (
                  <Tooltip title={filterText ? 'Limpe a busca para reordenar' : 'Arraste para reorganizar a ordem dos cardápios, categorias e itens'}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setReorderMode(true)}
                        disabled={!!filterText}
                        startIcon={<SwapVert />}
                        sx={{ borderColor: menuPalette.blue, color: menuPalette.blue, fontWeight: 800 }}
                      >
                        Reordenar
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button startIcon={<Add />} variant="contained" onClick={() => openForm('cardapio')} sx={{ boxShadow: '0 10px 22px rgba(31,79,191,0.28)' }}>
                  Novo cardápio
                </Button>
              </Box>
            </Box>
          

          {showComplementos && (
            <ComplementosPanel
              complementos={complementos}
              compForm={compForm}
              setCompForm={setCompForm}
              handleSaveComplemento={handleSaveComplemento}
              handleDeleteComplemento={handleDeleteComplemento}
            />
          )}

          {usarCardapioVirtual && showHeaderConfig && (
            <DesignPanel
              restauranteHeader={restauranteHeader}
              setRestauranteHeader={setRestauranteHeader}
              handleSaveRestauranteHeader={handleSaveRestauranteHeader}
              savingRestaurante={savingRestaurante}
              restaurante={restaurante}
            />
          )}
          <MenuTreePanel
            filteredCardapios={filteredCardapios}
            filterText={filterText}
            setFilterText={setFilterText}
            openForm={openForm}
            openAvailability={openAvailability}
            onDragEnd={onDragEnd}
            reorderMode={reorderMode}
            expandedCardapioId={expandedCardapioId}
            setExpandedCardapioId={setExpandedCardapioId}
            selectedChannelMeta={selectedChannelMeta}
            handleDelete={handleDelete}
            toggleItemActive={toggleItemActive}
          />
          
        </Box>
        {reorderMode && (
          <Paper
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(242,107,58,0.08)',
              border: `1px dashed ${menuPalette.orange}`,
              boxShadow: 'none',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <DragIndicatorIcon htmlColor={menuPalette.orange} />
                <Typography variant="body2" sx={{ color: menuPalette.ink }}>
                  <strong>Modo de ordenação ativo:</strong> arraste para reorganizar.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={cancelOrder} disabled={savingOrder}>Cancelar</Button>
                <Button size="small" variant="contained" color="secondary" onClick={persistOrder} disabled={savingOrder}>
                  {savingOrder ? 'Salvando...' : 'Salvar ordem'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
        </Box>
        <DashboardModals
          availabilityModal={availabilityModal}
          setAvailabilityModal={setAvailabilityModal}
          closeAvailability={closeAvailability}
          handleSaveAvailability={handleSaveAvailability}
          pedidoModal={pedidoModal}
          pedidoModalData={pedidoModalData}
          closePedidoModal={closePedidoModal}
          allItems={allItems}
          allComplementos={allComplementos}
          pedidoAddItem={pedidoAddItem}
          setPedidoAddItem={setPedidoAddItem}
          handleAddItemPedido={handleAddItemPedido}
          handleUpdateItemQtd={handleUpdateItemQtd}
          handleRemoveItemPedido={handleRemoveItemPedido}
          handleUpdateItemComplementos={handleUpdateItemComplementos}
          openConfirmDialog={openConfirmDialog}
          refreshPedidos={refreshPedidos}
          confirmDialog={confirmDialog}
          closeConfirmDialog={closeConfirmDialog}
          handleConfirmDialogConfirm={handleConfirmDialogConfirm}
          formState={formState}
          setFormState={setFormState}
          closeForm={closeForm}
          handleSaveForm={handleSaveForm}
          cardapios={cardapios}
          allCategorias={allCategorias}
        />
        <IfoodCatalogImportDialog
          open={ifoodImportOpen}
          onClose={() => setIfoodImportOpen(false)}
          merchantId={ifoodMerchantId}
          onImported={() => loadData({ silent: true })}
        />
    </>
  );
};

export default Dashboard;
